import React, { useEffect, useState } from "react";
import { Spin, Pagination, Empty, message } from "antd";
import { getMyPlaylists } from "@sharpana/auth/api_client/summarization_api";
import { getBookmarkCount } from "./BookmarksFeed/api";
import { useNavigate } from "react-router-dom";
import { YouTubeLogo } from "./RecommendationFeed/components/YtSharp";
import { BookFilled, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { AddPlaylist } from "./AddPlaylist";
import {
  playlistsContainer,
  emptyContainer,
  playlistItem,
  thumbnailContainer,
  playlistInfo,
  paginationContainer,
  darkLoadingContainer,
  bookmarkIcon,
  title,
  emptyStateContainer,
  emptyStateHeader,
  instructionsText,
  videoEmbedContainer,
  addPlaylistButton,
  addPlaylistModalContainer,
  addPlaylistModalContent,
  modalCloseButton,
  underlinedText,
  highlightedText,
  accentText,
  accentTextAlt,
  demoText,
  centeredUrlRequestContainer,
} from "./my_playlists_styles";
import { SummarizationURLRequest } from "./SummarizationURLRequest";

interface Video {
  id: string;
  title: string;
  thumbnail: string;
  url: string;
}

interface Playlist {
  id: string;
  title: string;
  description: string;
  video_count: number;
  videos: Video[];
}

interface PlaylistsResponse {
  results: Playlist[];
  total: number;
  total_pages: number;
  current_page: number;
  error?: string;
}

const BookmarksCard = ({ bookmarkCount }: { bookmarkCount: number }) => {
  const navigate = useNavigate();
  return (
    <>
      {bookmarkCount > 0 && (
        <div
          css={playlistItem}
          onClick={() => navigate("/bookmarks")}
          role="button"
          tabIndex={0}
        >
          <div css={thumbnailContainer}>
            <div className="placeholder">
              <BookFilled css={bookmarkIcon} />
            </div>
          </div>
          <div css={playlistInfo}>
            <h3>My Bookmarks</h3>
            <div className="video-count">
              {bookmarkCount} item{bookmarkCount !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const PlaylistsList = ({ playlists }: { playlists: Playlist[] }) => {
  const navigate = useNavigate();
  const handlePlaylistClick = (playlist: Playlist) => {
    navigate(`/playlist/${playlist.id}`);
  };
  return (
    <>
      {playlists.map((playlist) => (
        <div
          key={playlist.id}
          css={playlistItem}
          onClick={() => handlePlaylistClick(playlist)}
          role="button"
          tabIndex={0}
        >
          <div css={thumbnailContainer}>
            {playlist.videos?.[0]?.thumbnail ? (
              <img
                src={playlist.videos[0].thumbnail}
                alt={`${playlist.title || "Untitled Playlist"} thumbnail`}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = "/placeholder-thumbnail.jpg";
                }}
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.classList.remove("loading");
                  img.classList.add("loaded");
                }}
                className="loading"
              />
            ) : (
              <>
                <div className="placeholder">▶</div>
                <img
                  src="/placeholder-thumbnail.jpg"
                  alt="Playlist thumbnail placeholder"
                />
              </>
            )}
          </div>
          <div css={playlistInfo}>
            <h3>{playlist.title || "Untitled Playlist"}</h3>
            <p>{playlist.description || "No description available"}</p>
            <div className="video-count">
              {playlist.video_count} video
              {playlist.video_count !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export const InlineAddPlaylist = ({
  refreshPlaylists,
}: {
  refreshPlaylists: any;
}) => {
  const [showAddPlaylistComponent, setShowAddPlaylistComponent] =
    useState(false);

  const toggleAddPlaylistComponent = () => {
    const willShow = !showAddPlaylistComponent;
    setShowAddPlaylistComponent(willShow);

    // If we're showing the component, scroll to make it visible after it renders
    if (willShow) {
      // Use setTimeout to ensure the component is rendered before scrolling
      setTimeout(() => {
        const addPlaylistElement = document.querySelector(
          ".add-playlist-container"
        );
        if (addPlaylistElement) {
          addPlaylistElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };
  return (
    <div>
      <button
        css={addPlaylistButton}
        className={showAddPlaylistComponent ? "active" : ""}
        onClick={toggleAddPlaylistComponent}
        aria-label={showAddPlaylistComponent ? "Cancel" : "Add playlist"}
      >
        {showAddPlaylistComponent ? "Cancel" : "Add playlist"}
      </button>

      {/* Inline Add Playlist form when button is clicked */}
      {showAddPlaylistComponent && (
        <div css={emptyStateContainer} className="add-playlist-container">
          <AddPlaylist onSuccess={refreshPlaylists} />
        </div>
      )}
    </div>
  );
};

export const InvitationToConnectPlaylist = ({
  refreshPlaylists,
}: {
  refreshPlaylists: any;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleCollapse = () => {
    const willBeCollapsed = isCollapsed;
    setIsCollapsed(!willBeCollapsed);

    // If we're expanding (not collapsing), scroll this container into view
    if (willBeCollapsed) {
      // Use setTimeout to ensure the DOM has updated before scrolling
      setTimeout(() => {
        const containerElement = document.querySelector(
          `[data-container="invitation-container"]`
        );
        if (containerElement) {
          containerElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };
  return (
    <div css={emptyStateContainer} data-container="invitation-container">
      <header
        css={[emptyStateHeader, { cursor: "pointer" }]}
        onClick={toggleCollapse}
      >
        <span
          style={{
            display: "inline-block",
            marginRight: "8px",
            transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
            transition: "transform 0.3s ease",
          }}
        >
          ▶
        </span>
        Connect your <YouTubeLogo />
        YouTube
      </header>

      {!isCollapsed && (
        <>
          <AddPlaylist onSuccess={refreshPlaylists} />

          <div css={instructionsText}>
            {/* <span css={highlightedText}>Found a great video but got no time?</span> <br/>
                💡 Get the <span> "Aha!" moments</span>, <span css={accentText}>on your time</span>.
                <br /> */}
            <span css={highlightedText}>
              Tons of great content, not enough time? There's a better way.
            </span>{" "}
            <br />
            💡 Unlock those <span> "Aha!" moments</span>,<br />
            <span css={accentText}>
              on <strong>your</strong> time
            </span>
            .
            <br />
            <br />
            {/* <ol>
                  <li>Add the video to your YouTube playlist.</li>
                  <li>Get a summary on Sharpana.</li>
                </ol> */}
            Link a YouTube playlist, save videos and start getting sharper.{" "}
            <br />
            <span css={demoText}>See how in the 1-minute demo below</span>
            {/* <strong>See how it works in our 1-min demo:</strong> */}
            <br />
          </div>

          <div css={videoEmbedContainer}>
            <div
              style={{
                position: "relative",
                paddingBottom: "64.5933014354067%",
                height: 0,
              }}
            >
              <iframe
                src="https://www.loom.com/embed/09ac5d3d5b3f4a2ab65bb1efa9b446e6?sid=29649cdd-4870-4e18-b63d-d860480fec36"
                frameBorder="0"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              ></iframe>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
